import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { X } from 'lucide-react'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import { useUnitAssessmentCatalog, useCreateUnitAssessment } from '@/hooks/coaching/useUnitAssessments'
import { getApiErrorCode } from '@/utils/apiError'
import type { UaMode } from '@/api/unitAssessmentAdmin'
import type { CurriculumNodeView } from '@/types/coaching'
import {
  buildLessonPlacementOptions,
  LessonPlacementPicker,
  type LessonPlacementOption,
} from './LessonPlacementPicker'

interface Props {
  learnerId: number
  onClose: () => void
  onCreated?: (unitAssessmentId: number) => void
}

export type AnchorOption = LessonPlacementOption

/**
 * 단원평가 특강 생성 팝업 — 학년/학기/단원 세트 + 응시방식 + 진행 위치 선택.
 * 완료 레슨은 제외하고, 진행중/미시작 레슨 앞에 미리 예약할 수 있다.
 */
export function CreateUnitAssessmentModal({ learnerId, onClose, onCreated }: Props) {
  const { t } = useTranslation('coaching')
  const library = useLibrary(learnerId)
  const catalog = useUnitAssessmentCatalog()
  const create = useCreateUnitAssessment(learnerId)

  const [grade, setGrade] = useState<number | null>(null)
  const [semester, setSemester] = useState<number | null>(null)
  const [quizSetId, setQuizSetId] = useState<number | null>(null)
  const [anchorLessonId, setAnchorLessonId] = useState<number | null>(null)
  const [mode, setMode] = useState<UaMode>('PRINT')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const sets = catalog.data?.sets ?? []
  const grades = catalog.data?.grades ?? []
  const anchorOptions = useMemo(() => buildAnchorOptions(library.data?.tree ?? []), [library.data])
  const selectedAnchor = anchorOptions.find((opt) => opt.lessonId === anchorLessonId) ?? null

  useEffect(() => {
    if (anchorLessonId != null || anchorOptions.length === 0) return
    const defaultAnchor = anchorOptions.find((opt) => opt.status === 'in_progress') ?? anchorOptions[0]
    setAnchorLessonId(defaultAnchor.lessonId)
  }, [anchorLessonId, anchorOptions])

  const semesters = useMemo(
    () => Array.from(new Set(sets.filter((s) => s.grade === grade).map((s) => s.semester))).sort((a, b) => a - b),
    [sets, grade],
  )
  const unitSets = useMemo(
    () =>
      sets
        .filter((s) => s.grade === grade && s.semester === semester)
        .sort((a, b) => a.unitNo - b.unitNo),
    [sets, grade, semester],
  )

  const selectGrade = (g: number) => {
    setGrade(g)
    setSemester(null)
    setQuizSetId(null)
  }
  const selectSemester = (s: number) => {
    setSemester(s)
    setQuizSetId(null)
  }

  const canSubmit = quizSetId != null && anchorLessonId != null && !create.isPending

  const submit = async () => {
    if (quizSetId == null || anchorLessonId == null) return
    setErrorMsg(null)
    try {
      const summary = await create.mutateAsync({ quizSetId, mode, anchorLessonId })
      onCreated?.(summary.id)
      onClose()
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      const alreadyActive = getApiErrorCode(e) === 'UA_ALREADY_ACTIVE' || status === 409
      setErrorMsg(alreadyActive ? t('unitAssessment.create.errorConflict') : t('unitAssessment.create.error'))
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('unitAssessment.create.title')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('unitAssessment.create.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {selectedAnchor
                ? t('unitAssessment.create.anchor', { lesson: selectedAnchor.title })
                : t('unitAssessment.create.anchorPending')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('unitAssessment.create.close')}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="size-5" />
          </button>
        </header>

        {catalog.isLoading || library.isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500">{t('unitAssessment.create.loadingCatalog')}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {anchorOptions.length > 0 ? (
              <LessonPlacementPicker
                id="unit-assessment-anchor"
                label={t('unitAssessment.create.anchorLesson')}
                options={anchorOptions}
                value={anchorLessonId}
                onChange={setAnchorLessonId}
              />
            ) : (
              <Field label={t('unitAssessment.create.anchorLesson')}>
                <p className="mt-1 text-xs text-gray-400">{t('unitAssessment.create.noAnchor')}</p>
              </Field>
            )}

            {/* 학년 */}
            <Field label={t('unitAssessment.create.grade')}>
              <div className="flex flex-wrap gap-2">
                {grades.map((g) => (
                  <Chip key={g} active={grade === g} onClick={() => selectGrade(g)}>
                    {t('unitAssessment.create.gradeValue', { grade: g })}
                  </Chip>
                ))}
                {grades.length === 0 && <span className="text-sm text-gray-400">{t('unitAssessment.create.empty')}</span>}
              </div>
            </Field>

            {/* 학기 */}
            {grade != null && (
              <Field label={t('unitAssessment.create.semester')}>
                <div className="flex flex-wrap gap-2">
                  {semesters.map((s) => (
                    <Chip key={s} active={semester === s} onClick={() => selectSemester(s)}>
                      {t('unitAssessment.create.semesterValue', { semester: s })}
                    </Chip>
                  ))}
                </div>
              </Field>
            )}

            {/* 단원 세트 */}
            {grade != null && semester != null && (
              <Field label={t('unitAssessment.create.unit')} htmlFor="unit-assessment-set">
                <select
                  id="unit-assessment-set"
                  aria-label={t('unitAssessment.create.unit')}
                  value={quizSetId ?? ''}
                  onChange={(e) => setQuizSetId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">{t('unitAssessment.create.unitPlaceholder')}</option>
                  {unitSets.map((s) => (
                    <option key={s.quizSetId} value={s.quizSetId}>
                      {t('unitAssessment.create.unitOption', {
                        unitNo: s.unitNo,
                        unitName: s.unitName,
                        count: s.memberCount,
                      })}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {/* 응시 방식 */}
            <Field label={t('unitAssessment.create.mode')}>
              <div className="flex gap-2">
                {(['PRINT', 'ONLINE'] as UaMode[]).map((m) => (
                  <Chip key={m} active={mode === m} onClick={() => setMode(m)}>
                    {t(`unitAssessment.mode.${m}`)}
                  </Chip>
                ))}
              </div>
            </Field>

            {errorMsg && <p className="text-sm text-red-600" role="alert">{errorMsg}</p>}
          </div>
        )}

        <footer className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('unitAssessment.create.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={clsx(
              'rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white',
              !canSubmit && 'cursor-not-allowed opacity-50',
            )}
          >
            {create.isPending ? t('unitAssessment.create.creating') : t('unitAssessment.create.submit')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div>
      {htmlFor ? (
        <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-gray-600">{label}</label>
      ) : (
        <p className="mb-1.5 text-xs font-semibold text-gray-600">{label}</p>
      )}
      {children}
    </div>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
        active ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
      )}
    >
      {children}
    </button>
  )
}

export function buildAnchorOptions(tree: CurriculumNodeView[]): AnchorOption[] {
  return buildLessonPlacementOptions(tree, (status) => status !== 'completed')
}
